import { googlePayCredentialsAtom, venueAtom, userInfoAtom, paymentMethodsAtom, applePayUserAtom } from '@/state/global'
import { PaymentDataAtom } from '@/state/payment'
import {
  GOOGLE_PASE_BASE_REQUEST,
  GOOGLE_PAY_ALLOWED_CARD_AUTH_METHODS,
  GOOGLE_PAY_ALLOWED_CARD_NETWORKS,
  GOOGLE_PAY_ENV,
  GOOGLE_PAY_PAYMENT_METHOD_TYPE,
  IS_PROD,
  PaymentMethodInputTypes,
} from '@/utils/constants'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useState, useEffect } from 'react'

type UseGooglePayReturn = {
  handleGooglePayClick: (totalAmount?: string) => Promise<unknown>
  isGooglePaySupported: boolean
}

export function useGooglePay(): UseGooglePayReturn {
  const [isSupported, setIsSupported] = useState(false)
  const googlePayCredentials = useAtomValue(googlePayCredentialsAtom)
  const venue = useAtomValue(venueAtom)
  const storedUser = useAtomValue(userInfoAtom)
  const setApplePayUser = useSetAtom(applePayUserAtom)
  const [paymentMethods, setPaymentMethods] = useAtom(paymentMethodsAtom)
  const [paymentMethod, setPaymentMethod] = useAtom(PaymentDataAtom)
  const [googlePayClient, setGooglePayClient] = useState<any>(null)

  useEffect(() => {
    async function initializeGooglePay() {
      try {
        loadGooglePayClient() // This function should remain synchronous as it instantiates the client
        if (googlePayClient) {
          const isReadyToPayResponse = await googlePayClient.isReadyToPay({
            ...GOOGLE_PASE_BASE_REQUEST,
            allowedPaymentMethods: [baseCardPaymentMethod],
          })
          setIsSupported(isReadyToPayResponse.result && venue?.google_pay_enabled && venue?.payment_processor === 'fortis')
        }
      } catch (err) {
        console.error('Error checking Google Pay support:', err)
      }
    }

    initializeGooglePay()
  }, [googlePayClient])

  const tokenizationSpecification = {
    type: 'PAYMENT_GATEWAY',
    parameters: {
      gateway: googlePayCredentials?.googleGatewayId ?? '',
      gatewayMerchantId: googlePayCredentials?.googleGatewayMerchantId ?? '',
    },
  }

  // CARD payment method with required fields
  const baseCardPaymentMethod = {
    type: GOOGLE_PAY_PAYMENT_METHOD_TYPE,
    parameters: {
      allowedAuthMethods: GOOGLE_PAY_ALLOWED_CARD_AUTH_METHODS,
      allowedCardNetworks: GOOGLE_PAY_ALLOWED_CARD_NETWORKS,
      billingAddressRequired: true,
      billingAddressParameters: {
        phoneNumberRequired: true,
        format: 'MIN',
      },
    },
  }

  const cardPaymentMethod = {
    ...baseCardPaymentMethod,
    tokenizationSpecification: tokenizationSpecification,
  }

  const merchantInfo = {
    merchantId: googlePayCredentials?.googleMerchantId ?? '',
    merchantName: venue?.name ?? '',
    merchantOrigin: window.location.hostname,
    ...(IS_PROD ? { authJwt: googlePayCredentials?.googleJWT } : {}),
  }

  const loadGooglePayClient = () => {
    if (typeof window !== 'undefined' && googlePayClient === null) {
      const newGooglePayClient = new google.payments.api.PaymentsClient({
        environment: GOOGLE_PAY_ENV,
        paymentDataCallbacks: {
          onPaymentAuthorized: onPaymentAuthorized,
        },
      })
      setGooglePayClient(newGooglePayClient)
    }
  }

  function onPaymentAuthorized(paymentData: any): Promise<google.payments.api.PaymentAuthorizationResult> {
    return new Promise(function (resolve, reject) {
      resolve({ transactionState: 'SUCCESS' })
    })
  }

  // Prepare Google Pay payment data request
  const getGooglePaymentDataRequest = (totalAmount?: string) => {
    const paymentDataRequest = {
      ...GOOGLE_PASE_BASE_REQUEST,
      allowedPaymentMethods: [cardPaymentMethod],
      transactionInfo: {
        countryCode: 'US',
        currencyCode: 'USD',
        ...(totalAmount ? { totalPriceStatus: 'FINAL', totalPrice: totalAmount } : { totalPriceStatus: 'NOT_CURRENTLY_KNOWN' }),
      },
      merchantInfo: merchantInfo,
      callbackIntents: ['PAYMENT_AUTHORIZATION'],
      emailRequired: true,
    }

    return paymentDataRequest
  }

  const handleGooglePayClick = async (totalAmount?: string) => {
    return new Promise(async (resolve, reject) => {
      if (!googlePayClient) {
        return reject(new Error('Google Pay client is not available'))
      }

      try {
        const paymentDataRequest = getGooglePaymentDataRequest(totalAmount)
        // Customize paymentDataRequest based on your requirements
        const paymentData = await googlePayClient.loadPaymentData(paymentDataRequest)
        const data = paymentData.paymentMethodData

        let first_name = ''
        let last_name = ''
        if (data?.info?.billingAddress?.name) {
          const billingAddressUserName = data.info.billingAddress.name as string

          const nameParts = billingAddressUserName.split(' ')

          // Initialize first_name and last_name

          // Check the number of name parts
          if (nameParts.length === 2) {
            // If there are exactly 2 parts, assume the first part is the first_name
            first_name = nameParts[0]
            last_name = nameParts[1]
          } else if (nameParts.length >= 3) {
            // If there are 3 or more parts, assume the first part is the first_name
            // and the last part is the last_name, and ignore the middle part(s)
            if (nameParts[1].length === 1) {
              first_name = nameParts[0]
              last_name = nameParts[nameParts.length - 1]
            } else {
              first_name = `${nameParts[0]} ${nameParts[1]}`
              last_name = nameParts[nameParts.length - 1]
            }
          }
        }

        const userName = {
          first_name: first_name || storedUser?.first_name || '',
          last_name: last_name || storedUser?.last_name || '',
        }
        const paymentMethod = {
          network: data.info?.cardNetwork.toLowerCase() ?? '',
          name: `${userName.last_name.toUpperCase()}/${userName.first_name.toUpperCase()}`,
          pan_mask: `************${data.info?.cardDetails}`,
          input_mode: PaymentMethodInputTypes.FORTIS_GOOGLE_PAY_WEB,
          card_token: data.tokenizationData.token,
        }

        let cleanPhone = (data.info.billingAddress.phoneNumber.replace(/(?!^\+)\D/g, '') as string) ?? ''

        if (!cleanPhone.startsWith('+')) {
          cleanPhone = ''
        }

        // Update local storage and state
        localStorage.setItem('paymentMethods', JSON.stringify([paymentMethod, ...paymentMethods]))
        setPaymentMethod(paymentMethod)
        const paymentUser = {
          first_name: userName.first_name,
          last_name: userName.last_name,
          email: paymentData.email,
          phone: cleanPhone,
        }
        if (!storedUser?.phone) {
          setApplePayUser(paymentUser)
        }
        resolve({ paymentMethod: paymentMethod, paymentUser: paymentUser }) // Resolve the promise with the payment method
      } catch (error) {
        // Handle errors in payment authorization
        console.error('Failed to load Google Pay payment data:', error)
        reject(error) // Reject the promise if there's an error
      }
    })
  }

  return {
    handleGooglePayClick,
    isGooglePaySupported: isSupported,
  }
}
